export const GIF_EDIT_MIN_ADJUSTMENT = -100
export const GIF_EDIT_MAX_ADJUSTMENT = 100
export const GIF_EDIT_MIN_FILTER_INTENSITY = 0
export const GIF_EDIT_MAX_FILTER_INTENSITY = 100

export type GifEditFilter = 'none' | 'grayscale' | 'sepia' | 'vivid'

export type GifEditAppearance = Readonly<{
  brightness: number
  contrast: number
  saturation: number
  warmth: number
  filter: GifEditFilter
  filterIntensity: number
}>

type GifEditColorMatrixRow = readonly [number, number, number, number, number]

export type GifEditColorMatrix = readonly [
  GifEditColorMatrixRow,
  GifEditColorMatrixRow,
  GifEditColorMatrixRow,
  GifEditColorMatrixRow,
]

const IDENTITY_COLOR_MATRIX: GifEditColorMatrix = [
  [1, 0, 0, 0, 0],
  [0, 1, 0, 0, 0],
  [0, 0, 1, 0, 0],
  [0, 0, 0, 1, 0],
]

const GRAYSCALE_COLOR_MATRIX: GifEditColorMatrix = [
  [0.2126, 0.7152, 0.0722, 0, 0],
  [0.2126, 0.7152, 0.0722, 0, 0],
  [0.2126, 0.7152, 0.0722, 0, 0],
  [0, 0, 0, 1, 0],
]

const SEPIA_COLOR_MATRIX: GifEditColorMatrix = [
  [0.393, 0.769, 0.189, 0, 0],
  [0.349, 0.686, 0.168, 0, 0],
  [0.272, 0.534, 0.131, 0, 0],
  [0, 0, 0, 1, 0],
]

const VIVID_COLOR_MATRIX: GifEditColorMatrix = [
  [1.3148, -0.286, -0.0288, 0, 0],
  [-0.0852, 1.114, -0.0288, 0, 0],
  [-0.0852, -0.286, 1.3712, 0, 0],
  [0, 0, 0, 1, 0],
]

/** @see https://drafts.fxtf.org/filter-effects/#feColorMatrixElement saturate luminance weights */
const SATURATION_LUMINANCE_RED = 0.213
const SATURATION_LUMINANCE_GREEN = 0.715
const SATURATION_LUMINANCE_BLUE = 0.072

/** Red/blue channel tilt applied at the extremes of the warmth range. */
const WARMTH_MAX_CHANNEL_SHIFT = 0.25

const RGB_MIDPOINT = 127.5

function assertFiniteNumber(
  value: unknown,
  name: string,
): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number`)
  }
}

function assertIntegerInRange(
  value: unknown,
  minimum: number,
  maximum: number,
  name: string,
): asserts value is number {
  assertFiniteNumber(value, name)

  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new RangeError(
      `${name} must be an integer between ${minimum} and ${maximum}`,
    )
  }
}

const isGifEditFilter = (value: unknown): value is GifEditFilter =>
  value === 'none' ||
  value === 'grayscale' ||
  value === 'sepia' ||
  value === 'vivid'

const copyGifEditAppearance = (
  value: Readonly<Record<keyof GifEditAppearance, unknown>>,
): GifEditAppearance => {
  const { brightness, contrast, saturation, warmth, filter, filterIntensity } =
    value

  assertIntegerInRange(
    brightness,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'brightness',
  )
  assertIntegerInRange(
    contrast,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'contrast',
  )
  assertIntegerInRange(
    saturation,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'saturation',
  )
  assertIntegerInRange(
    warmth,
    GIF_EDIT_MIN_ADJUSTMENT,
    GIF_EDIT_MAX_ADJUSTMENT,
    'warmth',
  )

  if (!isGifEditFilter(filter)) {
    throw new RangeError('filter must be none, grayscale, sepia, or vivid')
  }

  assertIntegerInRange(
    filterIntensity,
    GIF_EDIT_MIN_FILTER_INTENSITY,
    GIF_EDIT_MAX_FILTER_INTENSITY,
    'filterIntensity',
  )

  return { brightness, contrast, saturation, warmth, filter, filterIntensity }
}

export const isIdentityGifEditAppearance = (
  appearance: GifEditAppearance,
): boolean =>
  appearance.brightness === 0 &&
  appearance.contrast === 0 &&
  appearance.saturation === 0 &&
  appearance.warmth === 0 &&
  (appearance.filter === 'none' || appearance.filterIntensity === 0)

const getPresetColorMatrix = (filter: GifEditFilter): GifEditColorMatrix => {
  switch (filter) {
    case 'none':
      return IDENTITY_COLOR_MATRIX
    case 'grayscale':
      return GRAYSCALE_COLOR_MATRIX
    case 'sepia':
      return SEPIA_COLOR_MATRIX
    case 'vivid':
      return VIVID_COLOR_MATRIX
  }
}

const createColorMatrix = (
  getEntry: (row: 0 | 1 | 2 | 3, column: 0 | 1 | 2 | 3 | 4) => number,
): GifEditColorMatrix => {
  const createRow = (row: 0 | 1 | 2 | 3): GifEditColorMatrixRow => [
    getEntry(row, 0),
    getEntry(row, 1),
    getEntry(row, 2),
    getEntry(row, 3),
    getEntry(row, 4),
  ]

  return [createRow(0), createRow(1), createRow(2), createRow(3)]
}

const lerpColorMatrixFromIdentity = (
  matrix: GifEditColorMatrix,
  amount: number,
): GifEditColorMatrix =>
  createColorMatrix(
    (row, column) =>
      IDENTITY_COLOR_MATRIX[row][column] * (1 - amount) +
      matrix[row][column] * amount,
  )

/**
 * Composes two affine color transforms so the inner one applies first,
 * treating each 4x5 matrix as a 5x5 matrix whose implied last row is
 * [0, 0, 0, 0, 1].
 */
const multiplyColorMatrices = (
  outer: GifEditColorMatrix,
  inner: GifEditColorMatrix,
): GifEditColorMatrix =>
  createColorMatrix((row, column) => {
    const composed =
      outer[row][0] * inner[0][column] +
      outer[row][1] * inner[1][column] +
      outer[row][2] * inner[2][column] +
      outer[row][3] * inner[3][column]

    return column === 4 ? composed + outer[row][4] : composed
  })

const getBrightnessColorMatrix = (brightness: number): GifEditColorMatrix => {
  const scale = (brightness + 100) / 100

  return [
    [scale, 0, 0, 0, 0],
    [0, scale, 0, 0, 0],
    [0, 0, scale, 0, 0],
    [0, 0, 0, 1, 0],
  ]
}

const getContrastColorMatrix = (contrast: number): GifEditColorMatrix => {
  const scale = (contrast + 100) / 100
  const offset = RGB_MIDPOINT * (1 - scale)

  return [
    [scale, 0, 0, 0, offset],
    [0, scale, 0, 0, offset],
    [0, 0, scale, 0, offset],
    [0, 0, 0, 1, 0],
  ]
}

const getSaturationColorMatrix = (saturation: number): GifEditColorMatrix => {
  const scale = (saturation + 100) / 100
  const red = SATURATION_LUMINANCE_RED * (1 - scale)
  const green = SATURATION_LUMINANCE_GREEN * (1 - scale)
  const blue = SATURATION_LUMINANCE_BLUE * (1 - scale)

  return [
    [red + scale, green, blue, 0, 0],
    [red, green + scale, blue, 0, 0],
    [red, green, blue + scale, 0, 0],
    [0, 0, 0, 1, 0],
  ]
}

const getWarmthColorMatrix = (warmth: number): GifEditColorMatrix => {
  const shift = (warmth / 100) * WARMTH_MAX_CHANNEL_SHIFT

  return [
    [1 + shift, 0, 0, 0, 0],
    [0, 1, 0, 0, 0],
    [0, 0, 1 - shift, 0, 0],
    [0, 0, 0, 1, 0],
  ]
}

export const getGifEditAppearanceColorMatrix = (
  appearance: GifEditAppearance,
): GifEditColorMatrix => {
  const { brightness, contrast, saturation, warmth, filter, filterIntensity } =
    copyGifEditAppearance(appearance)

  const preset =
    filter === 'none'
      ? IDENTITY_COLOR_MATRIX
      : lerpColorMatrixFromIdentity(
          getPresetColorMatrix(filter),
          filterIntensity / 100,
        )

  return [
    getBrightnessColorMatrix(brightness),
    getContrastColorMatrix(contrast),
    getSaturationColorMatrix(saturation),
    getWarmthColorMatrix(warmth),
  ].reduce(
    (composed, adjustment) => multiplyColorMatrices(adjustment, composed),
    preset,
  )
}
